import {
  api,
  INCOME_TYPE_LABELS,
  INCOME_TYPES,
  type IncomeRecord,
  type IncomeType,
  queryKeys,
} from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatUsd, TOKEN_LABEL } from '../../lib/format';
import { INCOME_TABS } from './incomeTabs';

const col = createColumnHelper<IncomeRecord>();

const DESCRIPTIONS: Partial<Record<IncomeType, string>> = {
  direct_referral: `Directs 1 and 2 pay 50%; direct 3 and above pay 30%, in ${TOKEN_LABEL} Token.`,
};

/** Flat ledger for the income types that have no tier/progress view of their own. */
export function IncomeLedger() {
  const { type: slug = 'direct-referral' } = useParams();
  const normalised = slug.replace(/-/g, '_');
  const type = ((INCOME_TYPES as readonly string[]).includes(normalised)
    ? normalised
    : 'direct_referral') as IncomeType;

  const { query, setPage } = useTableQuery({ defaultSort: 'created_at' });

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.income(type, query),
    queryFn: () => api.income.list(type, query),
  });

  const columns = useMemo(
    () => [
      col.accessor('from_user_code', { header: 'From', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('level', { header: 'Level', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'USD', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: 'LDS', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('description', { header: 'Details', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('created_at', { header: 'Credited', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title={INCOME_TYPE_LABELS[type]} description={DESCRIPTIONS[type]} tabs={INCOME_TABS} />
      <DataTable<IncomeRecord>
        title={INCOME_TYPE_LABELS[type]}
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        emptyLabel="No income of this type yet"
        onPageChange={setPage}
      />
    </>
  );
}
